
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { MainNav } from '@/components/MainNav';
import { HeroSection } from '@/components/home/HeroSection';
import { Testimonials } from '@/components/Testimonials';
import { StatsSection } from '@/components/home/StatsSection';
import { MarketPrices } from '@/components/MarketPrices';
import { ServerMarketPrices } from '@/components/ServerMarketPrices';
import { RecommendedDealers } from '@/components/home/RecommendedDealers';
import { BlogSection } from '@/components/blog/BlogSection';
import { ValuePropositionSection } from '@/components/home/ValuePropositionSection';
import { ValuationCard } from '@/components/ValuationCard';
import { PriceChart } from '@/components/PriceChart';
import { ServerPriceChart } from '@/components/ServerPriceChart';
import { useStore } from '@/store/useStore';
import { PageSEO } from '@/components/PageSEO';
import { isSSR } from '@/lib/isSSR';
import { 
  createServiceSchema, 
  createPriceProductSchema,
  createBreadcrumbsSchema
} from '@/utils/schemaUtils';
import {
  createFAQSchema,
  createFinancialServiceSchema,
  createAggregateRatingSchema,
  createRealTimeDataSchema,
  createContactPointSchema
} from '@/utils/homeSchemaUtils';

const Index = () => {
  const { isAdmin } = useAuth();
  const { fetchCurrentPricesData, goldPrice, silverPrice } = useStore();
  // Add state to track selected metal for sharing between components
  const [selectedMetal, setSelectedMetal] = useState<"gold" | "silver">("gold");
  
  // Get prices from server store during SSR, fallback to client store
  const serverStore = isSSR() ? (globalThis as any).__SERVER_STORE : null;
  const effectiveGoldPrice = serverStore?.rawGoldPrice || goldPrice || 0;
  const effectiveSilverPrice = serverStore?.rawSilverPrice || silverPrice || 0;
  
  useEffect(() => {
    // Only fetch prices on the client if not already loaded from server
    if (!isSSR() && !(globalThis as any).__SERVER_STORE?.metalPrices) {
      console.log('[Index] Fetching prices on client-side');
      fetchCurrentPricesData().catch(err => {
        console.error("Errore nel caricamento dei prezzi:", err);
      });
    }
  }, [fetchCurrentPricesData]);

  // Create enhanced schemas for home page
  const goldPriceSchema = createPriceProductSchema({
    name: "Oro 24KT",
    description: "Prezzo attuale dell'oro 24 carati al grammo",
    image: "https://www.gildy.it/images/gold-bar.jpg",
    price: effectiveGoldPrice,
    priceCurrency: "EUR",
    url: "https://www.gildy.it/grafici"
  });
  
  const silverPriceSchema = createPriceProductSchema({
    name: "Argento 999",
    description: "Prezzo attuale dell'argento 999 al grammo",
    image: "https://www.gildy.it/images/silver-bar.jpg", 
    price: effectiveSilverPrice,
    priceCurrency: "EUR",
    url: "https://www.gildy.it/grafici"
  });
  
  const gildyServiceSchema = createServiceSchema({
    name: "Gildy - Servizi per Metalli Preziosi",
    description: "Gildy è la piattaforma completa per monitorare in tempo reale i prezzi di oro e argento, trovare i migliori compro oro, calcolare valutazioni e ricevere notifiche personalizzate.",
    url: "https://www.gildy.it",
    provider: {
      name: "Gildy",
      url: "https://www.gildy.it"
    },
    areaServed: "Italia"
  });
  
  const breadcrumbsSchema = createBreadcrumbsSchema([
    { name: 'Home', url: '/' }
  ]);

  // Enhanced schemas
  const faqSchema = createFAQSchema();
  const financialServiceSchema = createFinancialServiceSchema();
  const aggregateRatingSchema = createAggregateRatingSchema();
  const realTimeDataSchema = createRealTimeDataSchema(effectiveGoldPrice, effectiveSilverPrice);
  const contactPointSchema = createContactPointSchema();
  
  // Enhanced keywords with long-tail
  const keywords = [
    "prezzo oro oggi",
    "quotazione oro tempo reale", 
    "prezzo argento aggiornato",
    "compro oro vicino a me",
    "valutazione oro gratuita",
    "oro usato quanto vale",
    "vendere oro al miglior prezzo",
    "monitoraggio prezzi metalli preziosi",
    "grafici oro argento",
    "quotazioni oro 24kt 18kt",
    "compro oro affidabili Italia",
    "calcolo valore oro grammo"
  ];

  // Resource hints for performance
  const resourceHints = {
    preconnect: [
      "https://fonts.googleapis.com",
      "https://www.google-analytics.com",
      "https://qldjmxfimvxskdbastkx.supabase.co"
    ],
    dnsPrefetch: [
      "https://fonts.gstatic.com",
      "https://www.googletagmanager.com"
    ],
    preload: [
      {
        href: "/images/hero-gold.webp",
        as: "image",
        type: "image/webp"
      }
    ]
  };

  // Geo-targeting for Italy
  const geoTargeting = {
    country: "IT",
    placename: "Italia",
    position: "41.9028;12.4964"
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <PageSEO 
        title="Gildy - Prezzi Oro e Servizi per Metalli Preziosi | Quotazioni Tempo Reale"
        description="Gildy è la piattaforma completa per monitorare in tempo reale i prezzi di oro e argento, trovare i migliori compro oro verificati, calcolare valutazioni gratuite e ricevere notifiche personalizzate. Dealer verificati in tutta Italia."
        canonicalPath="/"
        structuredData={[
          goldPriceSchema, 
          silverPriceSchema, 
          gildyServiceSchema, 
          breadcrumbsSchema,
          faqSchema,
          financialServiceSchema,
          aggregateRatingSchema,
          realTimeDataSchema,
          contactPointSchema
        ]}
        keywords={keywords}
        resourceHints={resourceHints}
        geoTargeting={geoTargeting}
        ogImage="https://www.gildy.it/images/gildy-og-home.jpg"
      />
      
      <MainNav />
      <main className="flex-1 container mx-auto py-8 px-4 md:px-6" role="main">
        {/* Enhanced semantic structure */}
        <header>
          <HeroSection />
        </header>
        
        <section aria-labelledby="stats-heading">
          <h2 id="stats-heading" className="sr-only">Statistiche Gildy</h2>
          <StatsSection />
        </section>
        
        <section className="my-16" aria-labelledby="prices-heading">
          <h2 id="prices-heading" className="sr-only">Prezzi Metalli Preziosi</h2>
          {isSSR() ? (
            <ServerMarketPrices />
          ) : (
            <MarketPrices onMetalChange={setSelectedMetal} />
          )}
        </section>
        
        <section className="my-16" aria-labelledby="charts-heading">
          <h2 id="charts-heading" className="sr-only">Grafici Andamento Prezzi</h2>
          {isSSR() ? (
            <ServerPriceChart title="Andamento Prezzi" />
          ) : (
            <PriceChart 
              title="Andamento Prezzi" 
              isHomePage={true}
            />
          )}
        </section>
        
        <section className="my-16" aria-labelledby="dealers-heading">
          <h2 id="dealers-heading" className="sr-only">Compro Oro Consigliati</h2>
          <RecommendedDealers initialMetal={selectedMetal} />
        </section>
        
        <section className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          <div>
            <ValuePropositionSection />
          </div>
          <div>
            <ValuationCard />
          </div>
        </section>
        
        <section aria-labelledby="blog-heading">
          <h2 id="blog-heading" className="sr-only">Ultime Notizie</h2>
          <BlogSection />
        </section>
        
        <section className="my-16" aria-labelledby="testimonials-heading">
          <h2 id="testimonials-heading" className="sr-only">Testimonianze</h2>
          <Testimonials />
        </section>

        {/* Development Debug Button - only visible to admins */}
        {isAdmin && (
          <div className="fixed bottom-4 right-4">
            <Button variant="outline" asChild>
              <Link to="/debug-supabase">
                🐞 Supabase Debug
              </Link>
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default Index;
